import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  model,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  PaperBreakpointService,
  PaperButtonComponent,
  PaperInputComponent,
  PaperSelectComponent,
  type PaperSelectOption,
} from '@ricoh-paper/angular';
import {
  PaperWorkspaceRailComponent,
  type PaperWorkspaceRailItem,
  type PaperWorkspaceRailSection,
} from '@ricoh-paper/angular/shell';
import type { AdminNavGroup } from '../../admin/config/admin-nav';
import { ANALYTICS_EVENTS } from '../../core/analytics/events';
import { AnalyticsService } from '../../core/services/analytics.service';
import {
  ALL_AREAS,
  buildRailSections,
  effectiveNavDomain,
  filterAdminNav,
  findNavItem,
  isNavFilterActive,
  navDomains,
  navItemIds,
  navStorage,
  railLeaves,
  readStoredNavDomain,
  writeStoredNavDomain,
} from './admin-nav-filter';
import {
  ACTIVE_RAIL_ROW_SELECTOR,
  RAIL_ROW_SELECTOR,
  RAIL_SCROLLER_SELECTOR,
  prefersReducedMotion,
  railScrollBehavior,
  revealRailRow,
} from './rail-scroll';

/** The key the side nav's fold is remembered under (`PAPER_WORKSPACE_RAIL_STATE`). */
export const ADMIN_RAIL_STATE_KEY = 'admin';

/**
 * The admin side nav: Paper's `paper-workspace-rail` (Louis's side nav, One
 * Paper P2) carrying Settings' `ADMIN_NAV`, with the rail filter above it and
 * the active row kept in view.
 *
 * WHY A WRAPPER. The rail takes a model and filters nothing itself: this
 * component owns the filter and hands the rail a filtered model. The filter
 * sits in the rail's head slot (`slot="head"`, Paper 0.39), inside the card
 * under the tools row; until 0.39 it was a card of its own above the rail.
 * The rail's behaviour — sections, inline sub-pages, arrow keys, the fold to
 * 48px and its tooltips, the drawer — is Paper's, untouched.
 *
 * THE FOLD is remembered under `ADMIN_RAIL_STATE_KEY` through
 * `PAPER_WORKSPACE_RAIL_STATE` (localStorage, `paper-shell.providers.ts`).
 * Folded, the filter is hidden AND not applied: a domain chosen while open
 * must not silently shorten the icon strip with no control on screen to undo
 * it.
 *
 * THE DRAWER. Below 640px the rail is a drawer over the page, opened by the
 * menu chip in the workspace nav (`[(drawerOpen)]`). The filter stays out of
 * it, hidden and not applied, as it was on phones before; ⌘K searches every
 * section from the nav.
 *
 * ACTIVE ROW IN VIEW. After every render that could move it — a navigation,
 * a hard load or deep link, the gated nav filling in as the viewer and flags
 * load, a filter change — the active row is centred in the rail's own
 * scroller if any part of it is hidden. See `rail-scroll.ts` for why it is not
 * `scrollIntoView`.
 */
@Component({
  selector: 'ws-admin-rail-nav',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    TranslocoPipe,
    PaperWorkspaceRailComponent,
    PaperButtonComponent,
    PaperInputComponent,
    PaperSelectComponent,
  ],
  host: {
    class: 'ws-admin-rail-nav',
    '[attr.data-collapsed]': 'collapsed() ? "" : null',
  },
  template: `
    <!-- Test ids (ruling 17): Paper derives settings.nav.item.<key> for every
         row, settings.nav.toggle and settings.nav.close from this one. -->
    <paper-workspace-rail
      class="rail-body"
      data-testid="settings.nav"
      mode="sidenav"
      [label]="label()"
      [sections]="sections()"
      [activeKey]="activeKey() ?? null"
      [stateKey]="stateKey"
      [(collapsed)]="collapsed"
      [(drawerOpen)]="drawerOpen"
      (selected)="onSelected($event)"
    >
      <!-- The filter in the rail's head slot (Paper 0.39), inside the card
           under the tools row: it stays put while the rows scroll, and Paper
           gives its fields the rail's pill. -->
      @if (filterShown()) {
        <div slot="head" class="rail-filter" role="search" [attr.aria-label]="'shell.rail_filter.search_label' | transloco">
          <paper-input
            class="rail-filter__search"
            data-testid="settings.nav.filter.search"
            type="search"
            size="sm"
            clearable="auto"
            autocomplete="off"
            [placeholder]="'shell.rail_filter.search_placeholder' | transloco"
            [ariaLabel]="'shell.rail_filter.search_label' | transloco"
            [ngModel]="query()"
            (ngModelChange)="onQuery($event)"
            (keydown)="onSearchKeydown($event)"
          />
          <paper-select
            class="rail-filter__domain"
            data-testid="settings.nav.filter.domain"
            size="sm"
            [ariaLabel]="'shell.rail_filter.domain_label' | transloco"
            [options]="domainOptions()"
            [ngModel]="domain()"
            (ngModelChange)="onDomain($event)"
          />
          @if (lostIn(); as lost) {
            <p class="rail-filter__lost">
              <span class="rail-filter__lost-text">{{ 'shell.rail_filter.you_are_in' | transloco: { area: lost } }}</span>
              <paper-button intent="ghost" size="xs" data-testid="settings.nav.filter.show-all" (click)="showAll()">{{ 'shell.rail_filter.show_all' | transloco }}</paper-button>
            </p>
          }
          @if (noMatch()) {
            <div class="rail-filter__empty" role="status">
              <p class="rail-filter__empty-text">
                @if (allAreas()) {
                  {{ 'shell.rail_filter.no_match' | transloco }}
                } @else {
                  {{ 'shell.rail_filter.no_match_in' | transloco: { domain: domain() } }}
                }
              </p>
              <div class="rail-filter__empty-actions">
                <paper-button intent="ghost" size="xs" data-testid="settings.nav.filter.clear" (click)="clearQuery()">{{ 'shell.rail_filter.clear' | transloco }}</paper-button>
                @if (!allAreas()) {
                  <paper-button intent="ghost" size="xs" data-testid="settings.nav.filter.search-all" (click)="searchAllAreas()">{{ 'shell.rail_filter.search_all' | transloco }}</paper-button>
                }
              </div>
            </div>
          }
        </div>
      }
    </paper-workspace-rail>
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      gap: var(--s-2);
      min-height: 0;
      height: 100%;
    }
    /* In the rail's head slot: Paper's slot pads it and gives the fields the
       rail's pill; this only stacks the parts, at the slot's own gap. */
    .rail-filter {
      display: flex;
      flex-direction: column;
      gap: var(--paper-workspace-rail-filter-gap, 8px);
    }
    .rail-filter__search,
    .rail-filter__domain {
      display: block;
      width: 100%;
    }
    .rail-filter__lost,
    .rail-filter__empty {
      margin: 0;
      padding: var(--s-2) 10px;
      border-radius: var(--radius-sm);
      background: var(--paper-inset, var(--surface-sunken));
      font-size: var(--type-meta-size, 12px);
      line-height: 1.4;
      color: var(--text-secondary);
    }
    .rail-filter__lost {
      display: flex;
      align-items: center;
      gap: var(--s-2);
    }
    .rail-filter__lost-text {
      flex: 1 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .rail-filter__empty-text {
      margin: 0 0 var(--s-1);
    }
    .rail-filter__empty-actions {
      display: flex;
      flex-wrap: wrap;
      gap: var(--s-1);
    }
    .rail-body {
      flex: 1 1 0;
      min-height: 0;
    }
  `,
})
export class AdminRailNavComponent {
  /** The gated nav — `visibleAdminNav(...)`, never the raw `ADMIN_NAV`. */
  readonly groups = input.required<readonly AdminNavGroup[]>();
  /** The active nav id for the current URL, from `matchActiveNav`. */
  readonly activeKey = input<string | undefined>(undefined);
  /** The `<nav>`'s accessible name and the side nav's caption. */
  readonly label = input.required<string>();
  /** Below 640px: the drawer is open. Two-way, opened by the nav's menu chip. */
  readonly drawerOpen = model(false);

  protected readonly stateKey = ADMIN_RAIL_STATE_KEY;
  /** Folded to icons; read from and written to `PAPER_WORKSPACE_RAIL_STATE` by the rail. */
  protected readonly collapsed = signal(false);

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  private readonly analytics = inject(AnalyticsService);
  private readonly transloco = inject(TranslocoService);
  private readonly breakpoints = inject(PaperBreakpointService);
  private readonly storage = navStorage();

  /** Below 640px the rail is a drawer, and the filter stays out of it. */
  private readonly drawer = this.breakpoints.isCompact;
  /** The filter is on screen, and so applied. */
  protected readonly filterShown = computed(() => !this.collapsed() && !this.drawer());

  private readonly _query = signal('');
  protected readonly query = this._query.asReadonly();
  /** The stored choice, as stored. `domain()` is what is applied. */
  private readonly _storedDomain = signal<string | null>(readStoredNavDomain(this.storage));

  protected readonly domains = computed(() => navDomains(this.groups()));
  protected readonly domain = computed(() => effectiveNavDomain(this._storedDomain(), this.domains()));
  protected readonly allAreas = computed(() => this.domain() === ALL_AREAS);

  private readonly filter = computed(() =>
    this.filterShown() ? { query: this._query(), domain: this.domain() } : { query: '', domain: ALL_AREAS },
  );
  private readonly filteredGroups = computed(() => filterAdminNav(this.groups(), this.filter()));
  protected readonly sections = computed<readonly PaperWorkspaceRailSection[]>(() =>
    buildRailSections(this.filteredGroups()),
  );

  /** Rows a search can open, in rail order: every routed leaf. */
  private readonly results = computed(() => railLeaves(this.sections()));

  protected readonly noMatch = computed(
    () => isNavFilterActive(this.filter()) && this.filteredGroups().length === 0,
  );

  /**
   * The active page's name when the filter has hidden it, so nobody is left
   * with a rail that does not contain where they are. Null when the page is
   * on screen, or is not in the nav at all (that is not the filter's doing).
   */
  protected readonly lostIn = computed(() => {
    const id = this.activeKey();
    if (!id || !isNavFilterActive(this.filter()) || this.noMatch()) return null;
    if (navItemIds(this.filteredGroups()).has(id)) return null;
    return findNavItem(this.groups(), id)?.label ?? null;
  });

  private readonly lang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });
  protected readonly domainOptions = computed<PaperSelectOption[]>(() => {
    this.lang();
    return [
      { value: ALL_AREAS, label: this.transloco.translate('shell.rail_filter.all_areas') },
      // Group names are rendered untranslated as the rail's own headings, so
      // the option reads exactly like the heading it selects.
      ...this.domains().map((d) => ({ value: d, label: d })),
    ];
  });

  /** First successful reveal is instant (a page load is not a motion the
   *  person made); every later one may animate. */
  private revealedOnce = false;

  constructor() {
    effect(() => {
      // Everything that can move the active row: where we are, and what the
      // rail is drawing (which also changes with the filter and gating).
      this.activeKey();
      this.sections();
      untracked(() =>
        afterNextRender(() => this.revealActiveRow(), { injector: this.injector }),
      );
    });
  }

  /** Centre the active row in the rail's own scroller, if it is not fully
   *  visible. Public for the spec; the effect is the only production caller. */
  revealActiveRow(): boolean {
    const root = this.host.nativeElement;
    const scroller = root.querySelector<HTMLElement>(RAIL_SCROLLER_SELECTOR);
    const row = scroller?.querySelector(ACTIVE_RAIL_ROW_SELECTOR);
    if (!scroller || !row) return false;
    const behavior = railScrollBehavior(!this.revealedOnce, prefersReducedMotion());
    this.revealedOnce = true;
    return revealRailRow(scroller, row, behavior);
  }

  protected onQuery(value: unknown): void {
    this._query.set(typeof value === 'string' ? value : '');
  }

  protected clearQuery(): void {
    this._query.set('');
  }

  protected onDomain(value: unknown): void {
    const next = typeof value === 'string' && value ? value : ALL_AREAS;
    const previous = this.domain();
    if (next === previous) return;
    this.setDomain(next);
    this.analytics.capture(ANALYTICS_EVENTS.ADMIN_NAV_FILTER_DOMAIN_CHANGED, {
      domain: next,
      previous,
      domain_count: this.domains().length,
    });
  }

  protected searchAllAreas(): void {
    this.setDomain(ALL_AREAS);
  }

  protected showAll(): void {
    this.analytics.capture(ANALYTICS_EVENTS.ADMIN_NAV_FILTER_SHOW_ALL, {
      active_id: this.activeKey() ?? '',
      domain: this.domain(),
      query_length: this._query().length,
    });
    this._query.set('');
    this.setDomain(ALL_AREAS);
  }

  private setDomain(domain: string): void {
    this._storedDomain.set(domain === ALL_AREAS ? null : domain);
    writeStoredNavDomain(this.storage, domain);
  }

  /**
   * Esc clears (and only swallows the key when there was something to clear,
   * so an Esc meant for an open popover still reaches it). ↓ hands focus to
   * the first row, where the rail's own arrow keys take over. Enter opens the
   * first result.
   */
  protected onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (!this._query()) return;
      event.preventDefault();
      event.stopPropagation();
      this._query.set('');
      return;
    }
    if (event.key === 'ArrowDown') {
      const first = this.host.nativeElement.querySelector<HTMLElement>(
        `${RAIL_SCROLLER_SELECTOR} ${RAIL_ROW_SELECTOR}`,
      );
      if (!first) return;
      event.preventDefault();
      first.focus();
      return;
    }
    if (event.key === 'Enter') {
      const target = this.results()[0];
      if (!this._query().trim() || !target?.route) return;
      event.preventDefault();
      this.captureResultOpened(target.key, 'enter');
      void this.router.navigateByUrl(target.route);
    }
  }

  /** A row was chosen. Records it as a nav selection, and as a filter result
   *  when a search is narrowing the rail. A parent's disclosure is the rail's
   *  own and is not reported here. */
  protected onSelected(item: PaperWorkspaceRailItem): void {
    const found = findNavItem(this.groups(), item.key);
    if (!found) return;
    this.analytics.capture(ANALYTICS_EVENTS.ADMIN_NAV_ITEM_SELECTED, {
      item_id: item.key,
      group: found.group,
    });
    if (this._query().trim()) this.captureResultOpened(item.key, 'click');
  }

  private captureResultOpened(key: string, via: 'enter' | 'click'): void {
    const results = this.results();
    this.analytics.capture(ANALYTICS_EVENTS.ADMIN_NAV_FILTER_RESULT_OPENED, {
      item_id: key,
      group: findNavItem(this.groups(), key)?.group ?? '',
      via,
      query_length: this._query().length,
      result_index: results.findIndex((r) => r.key === key),
      result_count: results.length,
      domain: this.domain(),
    });
  }
}
